/**
 * Navigation Service - A365 Core
 * 
 * Servicio centralizado para el control de elementos de navegación observable.
 * Permite a las aplicaciones comunicarse con el navbar para mostrar/ocultar botones
 * y definir sus comportamientos de forma reactiva.
 * 
 * @author A365 IDP
 * @version 1.0.0
 */

import { BehaviorSubject } from 'rxjs';

// Estado inicial de la navegación
const initialNavigationState = {
  backButton: {
    visible: false,
    enabled: true,
    icon: 'arrowLeft',
    variant: 'default',
    color: 'tertiary',
    size: 'medium',
    action: null, // Función que se ejecutará al hacer click
    tooltip: null
  },
  // Historial de navegación para tracking automático
  navigationHistory: {
    previousUrl: null,
    currentUrl: null,
    history: [] // Stack de URLs anteriores
  }
  // Aquí se pueden agregar más elementos de navegación en el futuro
  // closeButton: { ... },
  // customButtons: []
};

// Observable principal para el estado de navegación
export const navigationState$ = new BehaviorSubject(initialNavigationState);

/**
 * Clase para gestionar el estado de navegación
 */
class NavigationService {
  constructor() {
    this.state$ = navigationState$;
    this.initializeUrlTracking();
  }

  /**
   * Inicializar el tracking automático de URLs
   * @private
   */
  initializeUrlTracking() {
    // Establecer URL inicial
    this.updateCurrentUrl(window.location.pathname);

    // Escuchar cambios de navegación de single-spa
    window.addEventListener('single-spa:routing-event', this.handleUrlChange.bind(this));
    window.addEventListener('popstate', this.handleUrlChange.bind(this));
  }

  /**
   * Manejar cambios de URL
   * @private
   */
  handleUrlChange() {
    const newUrl = window.location.pathname;
    this.updateCurrentUrl(newUrl);
  }

  /**
   * Actualizar la URL actual y mantener historial
   * @param {string} newUrl - Nueva URL
   */
  updateCurrentUrl(newUrl) {
    const currentState = this.getCurrentState();
    const { navigationHistory } = currentState;

    // Solo actualizar si la URL realmente cambió
    if (navigationHistory.currentUrl === newUrl) {
      return;
    }

    const newHistory = [...navigationHistory.history];
    
    // Si hay una URL actual, moverla al historial
    if (navigationHistory.currentUrl) {
      newHistory.push(navigationHistory.currentUrl);
      
      // Mantener solo las últimas 10 URLs para evitar memory leaks
      if (newHistory.length > 10) {
        newHistory.shift();
      }
    }

    const newState = {
      ...currentState,
      navigationHistory: {
        previousUrl: navigationHistory.currentUrl,
        currentUrl: newUrl,
        history: newHistory
      }
    };

    this.state$.next(newState);
  }

  /**
   * Obtiene el estado actual de navegación
   * @returns {Object} Estado actual
   */
  getCurrentState() {
    return this.state$.getValue();
  }

  /**
   * Configura el botón de retroceso (back button)
   * @param {Object} config - Configuración del botón
   * @param {boolean} config.visible - Si el botón debe ser visible
   * @param {boolean} config.enabled - Si el botón está habilitado
   * @param {string} config.icon - Ícono a mostrar
   * @param {Function} config.action - Función a ejecutar al hacer click
   * @param {string} config.variant - Variante del botón
   * @param {string} config.color - Color del botón
   * @param {string} config.size - Tamaño del botón
   * @param {string} config.tooltip - Tooltip del botón
   */
  setBackButton(config) {
    const currentState = this.getCurrentState();
    const newState = {
      ...currentState,
      backButton: {
        ...currentState.backButton,
        ...config
      }
    };

    this.state$.next(newState);
  }

  /**
   * Muestra el botón de retroceso con una acción específica
   * @param {Function} action - Función a ejecutar al hacer click
   * @param {Object} options - Opciones adicionales del botón
   */
  showBackButton(action, options = {}) {
    this.setBackButton({
      visible: true,
      enabled: true,
      action,
      ...options
    });
  }

  hideBackButton() {
    this.setBackButton({
      visible: false,
      action: null
    });
  }

  /**
   * Habilita o deshabilita el botón de retroceso
   * @param {boolean} enabled - Si el botón debe estar habilitado
   */
  setBackButtonEnabled(enabled) {
    this.setBackButton({ enabled });
  }

  /**
   * Ejecuta la acción del botón de retroceso si está disponible
   */
  executeBackAction() {
    const currentState = this.getCurrentState();
    const { backButton } = currentState;
    
    if (backButton.visible && backButton.enabled && backButton.action) {
      try {
        backButton.action();
      } catch (error) {
        console.error('[NavigationService] Error al ejecutar acción del botón de retroceso:', error);
      }
    } else {
      console.warn('[NavigationService] No hay acción configurada para el botón de retroceso');
    }
  }

  /**
   * Resetea el estado de navegación al estado inicial
   */
  reset() {
    this.state$.next(initialNavigationState);
  }

  /**
   * Obtener la URL anterior
   * @returns {string|null} URL anterior o null si no hay historial
   */
  getPreviousUrl() {
    const currentState = this.getCurrentState();
    return currentState.navigationHistory.previousUrl;
  }

  /**
   * Obtener la URL actual
   * @returns {string|null} URL actual
   */
  getCurrentUrl() {
    const currentState = this.getCurrentState();
    return currentState.navigationHistory.currentUrl;
  }

  /**
   * Obtener el historial completo de navegación
   * @returns {Array} Array de URLs en el historial
   */
  getNavigationHistory() {
    const currentState = this.getCurrentState();
    return currentState.navigationHistory.history;
  }

  /**
   * Navegar a la URL anterior usando single-spa
   * @returns {boolean} true si había URL anterior, false si no
   */
  navigateToPrevious() {
    const previousUrl = this.getPreviousUrl();
    
    if (previousUrl) {
      // Usar navigateToUrl de single-spa si está disponible
      if (window.singleSpa && window.singleSpa.navigateToUrl) {
        window.singleSpa.navigateToUrl(previousUrl);
      } else {
        // Fallback a history.pushState para SPA navigation
        window.history.pushState(null, '', previousUrl);
        window.dispatchEvent(new PopStateEvent('popstate'));
      }
      
      return true;
    } else {
      console.warn('[NavigationService] No hay URL anterior disponible');
      return false;
    }
  }

  /**
   * Verificar si estamos en la app mi-voucher
   * @returns {boolean} true si estamos en mi-voucher
   * @private
   */
  isInMyVoucherApp() {
    const pathParts = window.location.pathname.split("/");
    const app = pathParts[2]; // geo/app/...
    return app === "mi-voucher";
  }

  /**
   * Obtener la URL del geo actual
   * @returns {string} La URL geo (ej: "ar", "mx", etc.)
   * @private
   */
  getCurrentGeo() {
    return window.location.pathname.split("/")[1] || "ar";
  }

  /**
   * Obtener URL de fallback cuando no hay URL anterior
   * @returns {string} URL a la que debe navegar
   * @private
   */
  getFallbackUrl() {
    if (this.isInMyVoucherApp()) {
      // Si estamos en mi-voucher, ir a la página principal de mi-voucher
      const geo = this.getCurrentGeo();
      return `/${geo}/mi-voucher`;
    } else {
      // Si no estamos en mi-voucher, ir a la home del geo actual
      const geo = this.getCurrentGeo();
      return `/${geo}`;
    }
  }

  /**
   * Mostrar botón de retroceso que navega automáticamente a la URL anterior
   * @param {Object} options - Opciones adicionales del botón
   */
  showBackButtonToPrevious(options = {}) {
    const previousUrl = this.getPreviousUrl();
    
    if (previousUrl) {
      // Hay URL anterior, usar navegación normal
      this.showBackButton(() => this.navigateToPrevious(), {
        tooltip: options.tooltip || 'Volver a la página anterior',
        ...options
      });
    } else {
      // No hay URL anterior, determinar fallback según la app actual
      const fallbackUrl = this.getFallbackUrl();
      const isInMyVoucher = this.isInMyVoucherApp();
      
      console.log('[NavigationService] No hay URL anterior, usando fallback:', {
        fallbackUrl,
        isInMyVoucher,
        currentUrl: window.location.pathname
      });
      
      this.showBackButton(() => {
        console.log('[NavigationService] Navegando a URL de fallback:', fallbackUrl);
        
        // Usar navigateToUrl de single-spa si está disponible
        if (window.singleSpa && window.singleSpa.navigateToUrl) {
          window.singleSpa.navigateToUrl(fallbackUrl);
        } else {
          // Fallback a history.pushState para SPA navigation
          window.history.pushState(null, '', fallbackUrl);
          window.dispatchEvent(new PopStateEvent('popstate'));
        }
      }, {
        tooltip: options.tooltip || (isInMyVoucher ? 'Ir a mi voucher' : 'Ir al inicio'),
        ...options
      });
    }
  }

  /**
   * Suscribirse a cambios en el estado de navegación
   * @param {Function} callback - Función que recibe el nuevo estado
   * @returns {Subscription} Suscripción que debe ser cancelada manualmente
   */
  subscribe(callback) {
    return this.state$.subscribe(callback);
  }

  /**
   * Obtener el observable del estado
   * @returns {BehaviorSubject} Observable del estado
   */
  getState$() {
    return this.state$;
  }
}

// Instancia singleton del servicio
export const navigationService = new NavigationService();

// Funciones de conveniencia para uso directo
export const setBackButton = (config) => navigationService.setBackButton(config);
export const showBackButton = (action, options) => navigationService.showBackButton(action, options);
export const hideBackButton = () => navigationService.hideBackButton();
export const executeBackAction = () => navigationService.executeBackAction();
export const resetNavigation = () => navigationService.reset();
